import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogPanel, DialogTitle, Description } from '@headlessui/react';
import { ViewColumnsIcon } from '@heroicons/react/24/outline';
import { ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/16/solid';
import { getAllColumns } from '../../../../../components/Table/columns/index';
import SharedButton from '../../../../../global/Button/Button';
import type { SummaryMatchStats } from '../../utils/matchEvidenceToSummary';
import { buildSummaryMatchGridColumns } from '../../utils/buildSummaryMatchGridColumns';
import { exportSummaryMatchTable } from '../../utils/exportSummaryMatchTable';
import { stringArraysEqual, visibilityModelsEqual } from '../../utils/recordEquality';
import { DOWNLOAD_EXTENSIONS } from '../../constants';
import { ColumnVisibilityModels } from '../../../../../components/Table/ColumnVisibilityModels';
import SummaryMatchGridView from './SummaryMatchGridView';
import SummaryMatchColumnsPanel from './SummaryMatchColumnsPanel';
import './SummaryMatchModal.css';

const MODAL_Z = 10050;

type SummaryMatchModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  matchedRows: Record<string, string>[];
  stats: SummaryMatchStats | null;
  tab: string;
  view: string;
  source: string;
  sourceWeights?: Array<{ name: string; score: number }>;
  summaryColumnVisibilityModel?: Record<string, boolean>;
  summaryColumnOrder?: string[];
  onClose: () => void;
};

function cloneVisibilityModel(model: Record<string, boolean> | undefined): Record<string, boolean> {
  if (!model) return {};
  return { ...model };
}

type SummaryMatchModalBodyProps = {
  isLoading: boolean;
  error: string | null;
  showGrid: boolean;
  columns: ReturnType<typeof buildSummaryMatchGridColumns>;
  matchedRows: Record<string, string>[];
  columnVisibilityModel: Record<string, boolean>;
  columnOrder: string[];
  showColumnsPanel: boolean;
  view: string;
  tab: string;
  source: string;
  onColumnVisibilityChange: (model: Record<string, boolean>) => void;
  onColumnOrderChange: (order: string[]) => void;
  onVisibilityFromSidebar: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
};

const SummaryMatchModalBody = memo(function SummaryMatchModalBody({
  isLoading,
  error,
  showGrid,
  columns,
  matchedRows,
  columnVisibilityModel,
  columnOrder,
  showColumnsPanel,
  view,
  tab,
  source,
  onColumnVisibilityChange,
  onColumnOrderChange,
  onVisibilityFromSidebar,
}: SummaryMatchModalBodyProps) {
  return (
    <div className="summary-match-modal-body">
      <div className="summary-match-modal-grid-pane">
        {isLoading && (
          <div className="flex h-full min-h-[12rem] items-center justify-center text-sm text-slate-600">
            Loading and matching Evidence with Summary…
          </div>
        )}

        {!isLoading && error && (
          <div className="flex h-full min-h-[12rem] items-center justify-center px-6 text-center text-sm text-red-700">
            {error}
          </div>
        )}

        {showGrid && (
          <SummaryMatchGridView
            columns={columns}
            matchedRows={matchedRows}
            columnVisibilityModel={columnVisibilityModel}
            columnOrder={columnOrder}
            onColumnVisibilityModelChange={onColumnVisibilityChange}
            onColumnOrderChange={onColumnOrderChange}
          />
        )}
      </div>

      {showColumnsPanel && Object.keys(columnVisibilityModel).length > 0 && (
        <SummaryMatchColumnsPanel
          view={view}
          tab={tab}
          source={source}
          columnVisibilityModel={columnVisibilityModel}
          setColumnVisibilityModel={onVisibilityFromSidebar}
          columnOrder={columnOrder}
          setColumnOrder={onColumnOrderChange}
        />
      )}
    </div>
  );
});

export default function SummaryMatchModal({
  isOpen,
  isLoading,
  error,
  matchedRows,
  stats,
  tab,
  view,
  source,
  sourceWeights = [],
  summaryColumnVisibilityModel,
  summaryColumnOrder,
  onClose,
}: SummaryMatchModalProps) {
  const [mounted, setMounted] = useState(false);
  const [showColumnsPanel, setShowColumnsPanel] = useState(true);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({});
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const downloadDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setShowDownloadDropdown(false);
      return;
    }

    const baseModel =
      summaryColumnVisibilityModel ?? ColumnVisibilityModels.getByViewTab(view, tab, false) ?? {};
    const nextModel = cloneVisibilityModel(baseModel);
    const nextOrder = summaryColumnOrder?.length ? [...summaryColumnOrder] : Object.keys(nextModel);

    setColumnVisibilityModel(nextModel);
    setColumnOrder(nextOrder);
  }, [isOpen, summaryColumnOrder, summaryColumnVisibilityModel, tab, view]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target as Node)) {
        setShowDownloadDropdown(false);
      }
    };

    if (showDownloadDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDownloadDropdown]);

  const allColumns = getAllColumns({ isEvidence: false, tab });

  const columnKeysSignature = useMemo(
    () => Object.keys(columnVisibilityModel).sort().join('\u0000'),
    [columnVisibilityModel],
  );

  const columns = useMemo(() => {
    if (!columnKeysSignature) return [];

    return buildSummaryMatchGridColumns({
      allColumns,
      summaryColumnVisibilityModel: columnVisibilityModel,
      summaryColumnOrder: columnOrder,
      sourceWeights,
      tab,
      view,
      source,
      includeHidden: true,
    });
  }, [columnKeysSignature, columnOrder, source, sourceWeights, tab, view]);

  const handleColumnVisibilityChange = useCallback((model: Record<string, boolean>) => {
    setColumnVisibilityModel(prev => (visibilityModelsEqual(prev, model) ? prev : model));
  }, []);

  const handleColumnOrderChange = useCallback((order: string[]) => {
    setColumnOrder(prev => (stringArraysEqual(prev, order) ? prev : order));
  }, []);

  const handleVisibilityFromSidebar = useCallback(
    (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => {
      setColumnVisibilityModel(prev => {
        const next = updater(prev ?? {});
        return visibilityModelsEqual(prev, next) ? prev : next;
      });
    },
    [],
  );

  const handleDownload = useCallback(
    (extension: 'tsv' | 'xlsx') => {
      if (!matchedRows.length || !columnKeysSignature) return;

      const exportColumns = buildSummaryMatchGridColumns({
        allColumns,
        summaryColumnVisibilityModel: columnVisibilityModel,
        summaryColumnOrder: columnOrder,
        sourceWeights,
        tab,
        view,
        source,
        includeHidden: false,
      });

      exportSummaryMatchTable({
        rows: matchedRows,
        columns: exportColumns,
        extension,
        view,
        tab,
        source,
      });
    },
    [
      allColumns,
      columnKeysSignature,
      columnOrder,
      columnVisibilityModel,
      matchedRows,
      source,
      sourceWeights,
      tab,
      view,
    ],
  );

  const statsLine = stats
    ? `${stats.matchedSummaryCount} of ${stats.summaryRowCount} Summary associations match ${stats.uniqueAssociationCount} filtered Evidence association${stats.uniqueAssociationCount !== 1 ? 's' : ''} (${stats.evidenceRowCount} Evidence row${stats.evidenceRowCount !== 1 ? 's' : ''}).`
    : null;

  const showGrid = !isLoading && !error && matchedRows.length > 0 && columns.length > 0;

  if (!mounted || !isOpen || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <Dialog open={isOpen} onClose={onClose} style={{ position: 'relative', zIndex: MODAL_Z }}>
      <div className="summary-match-modal-backdrop" style={{ zIndex: MODAL_Z }}>
        <DialogPanel className="summary-match-modal-panel">
          <div className="shrink-0 border-b px-6 py-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-xl font-bold text-slate-900">Summary match</DialogTitle>
                <Description className="mt-1 text-sm text-slate-600">
                  Summary associations that correspond to your currently filtered Evidence results.
                </Description>
                {statsLine && !isLoading && (
                  <p className="mt-2 text-sm font-medium text-slate-800">{statsLine}</p>
                )}
              </div>
              <div className="flex shrink-0 flex-wrap items-center">
                {showGrid && (
                  <div className="relative" ref={downloadDropdownRef}>
                    <SharedButton
                      type="button"
                      variant="secondaryOutline"
                      size="xs"
                      onClick={() => setShowDownloadDropdown(prev => !prev)}
                      className="mb-0"
                    >
                      <ArrowDownTrayIcon className="mr-1.5 h-4 w-4" />
                      Download
                      <ChevronDownIcon
                        className="ml-1.5 h-4 w-4 transition-transform duration-200"
                        style={{ transform: showDownloadDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </SharedButton>
                    {showDownloadDropdown && (
                      <div className="absolute right-0 top-full z-50 mt-1 flex min-w-[8rem] flex-col overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-md">
                        {DOWNLOAD_EXTENSIONS.map(extension => (
                          <button
                            key={extension.value}
                            type="button"
                            className="flex w-full items-center px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-100"
                            onClick={() => {
                              handleDownload(extension.value as 'tsv' | 'xlsx');
                              setShowDownloadDropdown(false);
                            }}
                          >
                            <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
                            {extension.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <SharedButton
                  type="button"
                  variant={showColumnsPanel ? 'secondary' : 'secondaryOutline'}
                  size="xs"
                  onClick={() => setShowColumnsPanel(prev => !prev)}
                  className="mb-0 mr-0"
                >
                  <ViewColumnsIcon className="mr-1.5 h-4 w-4" />
                  {showColumnsPanel ? 'Hide columns' : 'Show columns'}
                </SharedButton>
                <SharedButton variant="secondaryText" size="xs" onClick={onClose} className="mb-0">
                  Close
                </SharedButton>
              </div>
            </div>
          </div>

          <SummaryMatchModalBody
            isLoading={isLoading}
            error={error}
            showGrid={showGrid}
            columns={columns}
            matchedRows={matchedRows}
            columnVisibilityModel={columnVisibilityModel}
            columnOrder={columnOrder}
            showColumnsPanel={showColumnsPanel}
            view={view}
            tab={tab}
            source={source}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            onColumnOrderChange={handleColumnOrderChange}
            onVisibilityFromSidebar={handleVisibilityFromSidebar}
          />
        </DialogPanel>
      </div>
    </Dialog>,
    document.body,
  );
}
